"use client";

import React, { useState, useCallback, useMemo } from "react";
import {
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Text,
  Button,
  VStack,
  HStack,
  Flex,
  Tooltip,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { Plus, Trash2, Pencil } from "lucide-react";
import { TbCalendarSearch, TbCalendarUp } from "react-icons/tb";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { emailAgentService } from "../../email-services/agentService";
import { useToast } from "@chakra-ui/react";
import { CalendarAvailabilityModal } from "./CalendarAvailabilityModal";
import { BookAppointmentModal } from "./BookAppointmentModal";
import { CustomFunctionModal } from "./CustomFunctionModal";

type FunctionType =
  | "check_availability_cal"
  | "book_appointment_cal"
  | "custom";

interface FunctionConfig {
  type: string;
  name: string;
  description?: string;
  [key: string]: any;
}

interface EmailAgentFunctionsProps {
  agentId: string;
  initialFunctions: FunctionConfig[];
}

const toSnakeCase = (str: string): string => {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
};

const convertToolConfigToDbFormat = (
  config: FunctionConfig
): Record<string, any> => {
  const dbConfig: Record<string, any> = {};
  for (const [key, value] of Object.entries(config)) {
    const snakeKey = toSnakeCase(key);
    dbConfig[snakeKey] = value;
  }
  return dbConfig;
};

export const EmailAgentFunctions: React.FC<EmailAgentFunctionsProps> =
  React.memo(({ agentId, initialFunctions }) => {
    const [functions, setFunctions] =
      useState<FunctionConfig[]>(initialFunctions);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedFunction, setSelectedFunction] =
      useState<FunctionType | null>(null);
    const [editingFunction, setEditingFunction] = useState<{
      index: number;
      config: FunctionConfig;
    } | null>(null);
    const toast = useToast();

    const handleAddFunction = useCallback((type: FunctionType) => {
      setSelectedFunction(type);
      setEditingFunction(null);
    }, []);

    const handleEditFunction = useCallback(
      (index: number) => {
        const functionConfig = functions[index];
        setEditingFunction({ index, config: functionConfig });
        setSelectedFunction(functionConfig.type as FunctionType);
      },
      [functions]
    );

    const handleSaveFunction = useCallback(
      async (newFunction: FunctionConfig) => {
        let updatedFunctions: FunctionConfig[];
        if (editingFunction !== null) {
          updatedFunctions = functions.map((f, i) =>
            i === editingFunction.index ? newFunction : f
          );
        } else {
          updatedFunctions = [...functions, newFunction];
        }

        setIsLoading(true);
        try {
          const toolsForDb = updatedFunctions.map((f) =>
            JSON.stringify(convertToolConfigToDbFormat(f))
          );

          await emailAgentService.update(agentId, { tools: toolsForDb });
          setFunctions(updatedFunctions);

          toast({
            title: editingFunction
              ? "Function updated successfully"
              : "Function added successfully",
            status: "success",
            duration: 2000,
          });

          setSelectedFunction(null);
          setEditingFunction(null);
        } catch (error) {
          console.error("Error saving function:", error);
          toast({
            title: "Error saving function",
            status: "error",
            duration: 3000,
          });
        } finally {
          setIsLoading(false);
        }
      },
      [agentId, functions, editingFunction, toast]
    );

    const handleRemoveFunction = useCallback(
      async (index: number) => {
        const updatedFunctions = functions.filter((_, i) => i !== index);
        setIsLoading(true);
        try {
          const toolsForDb = updatedFunctions.map((f) =>
            JSON.stringify(convertToolConfigToDbFormat(f))
          );

          await emailAgentService.update(agentId, { tools: toolsForDb });
          setFunctions(updatedFunctions);

          toast({
            title: "Function removed successfully",
            status: "success",
            duration: 2000,
          });
        } catch (error) {
          console.error("Error removing function:", error);
          toast({
            title: "Error removing function",
            status: "error",
            duration: 3000,
          });
        } finally {
          setIsLoading(false);
        }
      },
      [agentId, functions, toast]
    );

    const getFunctionIcon = useCallback((type: string) => {
      switch (type) {
        case "check_availability_cal":
          return <TbCalendarSearch size={16} />;
        case "book_appointment_cal":
          return <TbCalendarUp size={16} />;
        case "custom":
          return (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M4.33337 14.6667V10"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit={10}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4.33337 3.33398V1.33398"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit={10}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M11.6666 14.666V12.666"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit={10}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M11.6666 6.00065V1.33398"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit={10}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M6.33337 4.66732V8.66732C6.33337 9.40065 6.00004 10.0007 5.00004 10.0007H3.66671C2.66671 10.0007 2.33337 9.40065 2.33337 8.66732V4.66732C2.33337 3.93398 2.66671 3.33398 3.66671 3.33398H5.00004C6.00004 3.33398 6.33337 3.93398 6.33337 4.66732Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit={10}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13.6666 7.33333V11.3333C13.6666 12.0667 13.3333 12.6667 12.3333 12.6667H11C9.99996 12.6667 9.66663 12.0667 9.66663 11.3333V7.33333C9.66663 6.6 9.99996 6 11 6H12.3333C13.3333 6 13.6666 6.6 13.6666 7.33333Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit={10}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          );
        default:
          return null;
      }
    }, []);

    const functionsList = useMemo(
      () => (
        <VStack spacing={2} align="stretch">
          {functions.map((func, index) => (
            <Flex
              key={index}
              justify="space-between"
              align="center"
              p={1.5}
              bg="gray.50"
              borderRadius="md"
              minHeight="32px"
            >
              <HStack spacing={2} flex={1} overflow="hidden">
                {getFunctionIcon(func.type)}
                <Text fontSize="xs" isTruncated color="gray.800">
                  {func.name}
                </Text>
              </HStack>

              <HStack spacing={1} flexShrink={0}>
                <Tooltip label="Edit">
                  <IconButton
                    aria-label="Edit"
                    icon={<Pencil size={12} />}
                    size="xs"
                    variant="ghost"
                    onClick={() => handleEditFunction(index)}
                    isDisabled={isLoading}
                    color="gray.700"
                    _hover={{ bg: "orange.50", color: "orange.700" }}
                    _active={{ bg: "orange.100" }}
                    _focusVisible={{
                      boxShadow: "0 0 0 1px var(--chakra-colors-orange-500)",
                    }}
                  />
                </Tooltip>

                <Tooltip label="Remove">
                  <IconButton
                    aria-label="Remove"
                    icon={<Trash2 size={12} />}
                    size="xs"
                    variant="ghost"
                    onClick={() => handleRemoveFunction(index)}
                    isDisabled={isLoading}
                    color="gray.700"
                    _hover={{ bg: "orange.50", color: "orange.700" }}
                    _active={{ bg: "orange.100" }}
                    _focusVisible={{
                      boxShadow: "0 0 0 1px var(--chakra-colors-orange-500)",
                    }}
                  />
                </Tooltip>
              </HStack>
            </Flex>
          ))}
        </VStack>
      ),
      [
        functions,
        handleEditFunction,
        handleRemoveFunction,
        getFunctionIcon,
        isLoading,
      ]
    );

    const selectOptions = useMemo(
      () => [
        {
          value: "check_availability_cal",
          label: "Check Calendar Availability (Cal.com)",
          icon: <TbCalendarSearch size={16} />,
        },
        {
          value: "book_appointment_cal",
          label: "Book on the Calendar (Cal.com)",
          icon: <TbCalendarUp size={16} />,
        },
        {
          value: "custom",
          label: "Custom Function",
          icon: (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M4.33337 14.6667V10"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit={10}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4.33337 3.33398V1.33398"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit={10}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M11.6666 14.666V12.666"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit={10}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M11.6666 6.00065V1.33398"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit={10}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M6.33337 4.66732V8.66732C6.33337 9.40065 6.00004 10.0007 5.00004 10.0007H3.66671C2.66671 10.0007 2.33337 9.40065 2.33337 8.66732V4.66732C2.33337 3.93398 2.66671 3.33398 3.66671 3.33398H5.00004C6.00004 3.33398 6.33337 3.93398 6.33337 4.66732Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit={10}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13.6666 7.33333V11.3333C13.6666 12.0667 13.3333 12.6667 12.3333 12.6667H11C9.99996 12.6667 9.66663 12.0667 9.66663 11.3333V7.33333C9.66663 6.6 9.99996 6 11 6H12.3333C13.3333 6 13.6666 6.6 13.6666 7.33333Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit={10}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ),
        },
      ],
      []
    );

    return (
      <AccordionItem border="none">
        <h2>
          <AccordionButton
            py={3}
            px={4}
            bg="transparent"
            _hover={{ bg: "orange.50" }}
            _active={{ bg: "orange.100" }}
            _focusVisible={{
              boxShadow: "0 0 0 1px var(--chakra-colors-orange-500)",
            }}
          >
            <Box
              flex="1"
              textAlign="left"
              fontSize="sm"
              display="flex"
              alignItems="center"
              gap={2}
              color="orange.600"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M3.25 3.25H9.25V9.25H3.25V3.25ZM3.25 10.75H9.25V16.75H3.25V10.75ZM10.75 3.25H16.75V9.25H10.75V3.25ZM10.75 10.75H16.75V16.75H10.75V10.75ZM12.25 4.75V7.75H15.25V4.75H12.25ZM12.25 12.25V15.25H15.25V12.25H12.25ZM4.75 4.75V7.75H7.75V4.75H4.75ZM4.75 12.25V15.25H7.75V12.25H4.75Z"
                  fill="currentColor"
                />
              </svg>
              Functions
            </Box>
            <AccordionIcon color="orange.600" />
          </AccordionButton>
        </h2>

        <AccordionPanel pb={4} bg="gray.100">
          <VStack spacing={3} align="stretch">
            <Text fontSize="xs" color="gray.600">
              Enable your agent with Cal.com calendar capabilities.
            </Text>

            {functionsList}

            <Box>
              <Text mb={2} fontWeight="medium" color="gray.800">
                Add a function
              </Text>

              <Menu>
                <MenuButton
                  as={Button}
                  leftIcon={<Plus size={14} />}
                  rightIcon={<ChevronDownIcon />}
                  size="xs"
                  variant="outline"
                  colorScheme="orange"
                  bg="transparent"
                  color="gray.800"
                  borderColor="orange.300"
                  _hover={{ bg: "orange.50", borderColor: "orange.400" }}
                  _active={{ bg: "orange.100", borderColor: "orange.500" }}
                  _expanded={{ bg: "white", borderColor: "orange.500" }}
                  _focusVisible={{
                    borderColor: "orange.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-orange-500)",
                  }}
                  width="100%"
                  isDisabled={isLoading}
                >
                  Add
                </MenuButton>

                <MenuList
                  bg="gray.100"
                  borderColor="orange.200"
                  maxH="220px"
                  overflowY="auto"
                  sx={{
                    scrollbarWidth: "thin",
                    scrollbarColor:
                      "var(--chakra-colors-orange-400) transparent",
                    "&::-webkit-scrollbar": { width: "6px" },
                    "&::-webkit-scrollbar-track": {
                      background: "transparent",
                      borderRadius: "8px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "var(--chakra-colors-orange-400)",
                      borderRadius: "8px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "var(--chakra-colors-orange-500)",
                    },
                  }}
                >
                  {selectOptions.map((option) => (
                    <MenuItem
                      key={option.value}
                      onClick={() =>
                        handleAddFunction(option.value as FunctionType)
                      }
                      icon={option.icon}
                      bg="gray.100"
                      color="gray.800"
                      fontSize="sm"
                      _hover={{ bg: "orange.50" }}
                      _focus={{ bg: "orange.50" }}
                      _active={{ bg: "orange.100" }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </Box>
          </VStack>

          <CalendarAvailabilityModal
            isOpen={selectedFunction === "check_availability_cal"}
            onClose={() => {
              setSelectedFunction(null);
              setEditingFunction(null);
            }}
            onSave={handleSaveFunction}
            initialValues={editingFunction?.config}
          />

          <BookAppointmentModal
            isOpen={selectedFunction === "book_appointment_cal"}
            onClose={() => {
              setSelectedFunction(null);
              setEditingFunction(null);
            }}
            onSave={handleSaveFunction}
            initialValues={editingFunction?.config}
          />

          <CustomFunctionModal
            isOpen={selectedFunction === "custom"}
            onClose={() => {
              setSelectedFunction(null);
              setEditingFunction(null);
            }}
            onSave={handleSaveFunction}
            initialValues={editingFunction?.config}
          />
        </AccordionPanel>
      </AccordionItem>
    );
  });
